import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Phone-shaped Telegram-style chat mockup for the Features grid.
 * Each variant ('mood' | 'followup' | 'people') maps to a chat conversation
 * in t.ft_chats[0..2], showing 2 bubbles + (optionally) a proactive tag.
 *
 * Designed to sit on the lime (#dcf57b) Features panel — dark phone, light bubbles.
 */
const VARIANT_INDEX = { mood: 0, followup: 1, people: 2 };

function VoiceBubble({ duration }) {
  // Simple voice-note visual: play triangle + waveform bars + duration
  const bars = [3, 5, 4, 6, 3, 5, 7, 4, 6, 3, 5];
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center shrink-0">
        <span className="block w-0 h-0 ml-[2px] border-y-[4px] border-y-transparent border-l-[6px] border-l-white" />
      </span>
      <div className="flex items-center gap-[2px]" aria-hidden="true">
        {bars.map((h, i) => (
          <span key={i} className="block w-[2px] rounded-full bg-white/55" style={{ height: `${h * 1.4}px` }} />
        ))}
      </div>
      <span className="font-body text-[11px] text-white/55 lowercase">{duration}</span>
    </div>
  );
}

function Bubble({ msg, t }) {
  const isMe = msg.from === 'you';
  const isProactive = !!msg.proactive;

  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[88%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
        {isProactive && (
          <div className="mb-1.5 px-2 py-0.5 rounded-full bg-ev-lime/20 border border-ev-lime/40">
            <span className="font-body text-[8.5px] uppercase tracking-[0.18em] text-ev-lime">
              {t.fu_chat_proactive_tag}
            </span>
          </div>
        )}
        <div
          className={`px-3.5 py-2 rounded-[1.1rem] ${
            isMe
              ? 'bg-white/10 text-white rounded-br-md'
              : isProactive
              ? 'bg-ev-lime text-ev-ink rounded-bl-md'
              : 'bg-white text-ev-ink rounded-bl-md'
          }`}>
          {msg.type === 'voice' ? (
            <VoiceBubble duration={msg.duration} />
          ) : (
            <p className="font-body text-[12.5px] leading-snug">{msg.text}</p>
          )}
        </div>
        {msg.time && (
          <span className="font-body text-[10px] mt-1 lowercase text-white/40">{msg.time}</span>
        )}
      </div>
    </div>
  );
}

export default function FeaturePhoneMock({ variant }) {
  const { t } = useLanguage();
  const chat = t.ft_chats?.[VARIANT_INDEX[variant] ?? 0];
  if (!chat) return null;

  return (
    <div className="relative w-full max-w-[360px] mx-auto" data-testid={`feature-phone-${variant}`}>
      <div className="h-[560px] rounded-[2.25rem] bg-ev-ink border border-ev-ink/80 shadow-[0_30px_60px_-20px_rgba(10,10,10,0.4)] overflow-hidden flex flex-col">
        {/* Notch */}
        <div className="h-6 flex items-center justify-center shrink-0">
          <div className="w-16 h-1.5 rounded-full bg-white/10" />
        </div>

        {/* Header */}
        <div className="px-4 pt-2 pb-3 flex items-center gap-2.5 border-b border-white/10 shrink-0">
          <div className="w-7 h-7 rounded-full bg-ev-lime flex items-center justify-center">
            <span className="font-display text-ev-ink text-[11px] lowercase">e</span>
          </div>
          <div className="leading-tight">
            <p className="font-body text-[12px] text-white lowercase">{t.fu_chat_name}</p>
            <p className="font-body text-[10px] text-white/40 lowercase">{t.fu_chat_status}</p>
          </div>
        </div>

        {/* Bubbles — top-anchored */}
        <div className="px-3.5 pt-4 pb-3 space-y-3">
          {chat.bubbles.map((msg, i) => (
            <Bubble key={i} msg={msg} t={t} />
          ))}
        </div>

        {/* Spacer pushes compose bar to the bottom — keeps all phones equal-height */}
        <div className="flex-1" />

        {/* Compose bar — anchored at bottom */}
        <div className="mx-3.5 mb-4 px-3 py-2 rounded-full bg-white/[0.05] border border-white/10 flex items-center gap-2 shrink-0">
          <span className="block w-3.5 h-3.5 rounded-full bg-white/15" aria-hidden="true" />
          <span className="font-body text-[11px] text-white/35 lowercase tracking-wide">{t.fu_chat_compose}</span>
          <span className="ml-auto block w-4 h-4 rounded-full bg-ev-lime/80" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}
